// Everything we do for this client, nested the way the money model actually nests:
// retainer → job → publications, with the retainer's signed agreement at the top of
// its own fold.
//
// THE FLAT TABS THIS REPLACES. Publications and agreements used to be top-level tabs
// on the client card, which put them at the same level as the client's whole body of
// work while both are in fact properties of something further down. A publication
// belongs to a job; an agreement belongs to a retainer; neither belongs to the client
// directly. Nesting them puts each one where it actually lives, so opening a retainer
// shows that engagement whole — its contract, its topics, and each topic's coverage —
// instead of three flat lists that have to be cross-referenced by eye.
//
// WHAT THAT COSTS, stated plainly: the flat publications tab existed so nobody had to
// walk the jobs to see coverage, and nesting reinstates that walk. Two things pay it
// back. Every job row carries its publication COUNT whether open or shut, so which
// jobs hold coverage is readable without opening any of them; and expand-all restores
// the old everything-at-once view in one click.
//
// FOUR READS, ONE LEVEL, NO FETCHING ON EXPAND. The whole tree is assembled here from
// four by-client calls and distributed downward by id — publications carry `job_id`,
// agreements carry `retainer_id`. Fetching inside each fold instead would turn a client
// with six retainers into twenty-five requests and make every expand wait on the
// network. Opening a fold here costs nothing at all.
//
// THE TREE SHOWS LIVE WORK BY DEFAULT, one toggle for the whole card. A client of three
// years has a long tail of finished jobs, and folds full of them bury the handful still
// running. The toggle is at card level rather than per retainer because "what is still
// open for this client" is one question asked of the whole card, not of each engagement
// separately. Each retainer's badge keeps counting ALL its jobs either way, so the
// header never contradicts what the toggle is hiding.
import { useMemo, useState } from 'react';
import useAsync from '../../hooks/useAsync';
import useCollapsedGroups from '../../hooks/useCollapsedGroups';
import { jobsApi, retainersApi, publicationsApi } from '../../api';
import he from '../../i18n/he';
import AsyncBoundary from '../../components/Loader';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import ViewToggle from '../../components/ViewToggle';
import { isJobDone } from '../../components/StatusBadge';
import PublicationModal from '../../components/PublicationModal';
import RetainerBlock from './work/RetainerBlock';
import JobBlock from './work/JobBlock';
import { isProject } from '../../components/engagement';

// A retainer and a job can both be id 3, so the fold map namespaces them.
const rKey = (id) => `r:${id}`;
const jKey = (id) => `j:${id}`;

// Retainers open, jobs closed. Opening the tab should show each engagement's contract
// and the list of what is under it; coverage is one more click. Same shape as the
// board's rule, where a group's default depends on whether it has anything to say.
const RETAINER_DEFAULT = false; // collapsed = false → expanded
const JOB_DEFAULT = true;

const byId = (rows, key) => {
  const map = new Map();
  for (const row of rows) {
    const k = String(row[key]);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(row);
  }
  return map;
};

export default function WorkTab({ clientId }) {
  // One toggle for the whole card: the archive is a view of this client's contracts,
  // not of one retainer. It re-runs the single agreements read rather than asking each
  // fold to fetch its own.
  const [archived, setArchived] = useState(false);
  // A VIEW filter over the tree, applied to both levels at once. It costs no fetch: the
  // jobs read already returns every job of this client, finished ones included.
  const [showDone, setShowDone] = useState(false);
  const [editingPublication, setEditingPublication] = useState(null);
  const { isCollapsed, setCollapsed, setAll } = useCollapsedGroups('pressbooks.clientcard.collapsed');

  const retainersState = useAsync(() => retainersApi.byClient(clientId), [clientId]);
  const jobsState = useAsync(() => jobsApi.list(), [clientId]);
  const publicationsState = useAsync(() => publicationsApi.byClient(clientId), [clientId]);
  // The work content writers sent in (064), client-wide and grouped per job below — one
  // read for the whole tree, the same shape the publications above already use. Without
  // it a writer's upload is stored and shown nowhere: every screen here predates the table.
  const workFilesState = useAsync(() => jobsApi.clientWorkFiles(clientId), [clientId]);
  const agreementsState = useAsync(
    () => retainersApi.clientAgreements(clientId, { archived }), [clientId, archived]
  );

  const retainers = useMemo(() => retainersState.data || [], [retainersState.data]);
  // Two kinds of engagement, one per section (migration 067): the monthly retainers, and
  // the one-off projects — each with its jobs beneath it, drawn by the same block.
  const monthly = useMemo(() => retainers.filter((r) => !isProject(r)), [retainers]);
  const projects = useMemo(() => retainers.filter(isProject), [retainers]);
  const jobs = useMemo(
    () => (jobsState.data || []).filter((j) => String(j.client_id) === String(clientId)),
    [jobsState.data, clientId]
  );

  // The distribution maps. `retainer_id` is null on a standalone job, so it simply
  // never lands in the retainer map — which is what makes the standalone list a
  // filter rather than a second query.
  const jobsByRetainer = useMemo(() => byId(jobs, 'retainer_id'), [jobs]);
  const pubsByJob = useMemo(() => byId(publicationsState.data || [], 'job_id'), [publicationsState.data]);
  const workFilesByJob = useMemo(() => byId(workFilesState.data || [], 'job_id'), [workFilesState.data]);
  const agreementsByRetainer = useMemo(
    () => byId(agreementsState.data || [], 'retainer_id'), [agreementsState.data]
  );

  // Filtered for DISPLAY only, and never used for a count: `jobsByRetainer` above still
  // holds every job, so a retainer's badge says how many jobs it has rather than how
  // many the current tab is showing.
  const standalone = useMemo(
    () => jobs.filter((j) => !j.retainer_id && isJobDone(j.status) === showDone),
    [jobs, showDone]
  );
  const publicationsFor = (jobId) => pubsByJob.get(String(jobId)) || [];
  const workFilesFor = (jobId) => workFilesByJob.get(String(jobId)) || [];

  const loading = retainersState.loading || jobsState.loading
    || publicationsState.loading || agreementsState.loading || workFilesState.loading;
  const error = retainersState.error || jobsState.error
    || publicationsState.error || agreementsState.error || workFilesState.error;

  // Expand-all / collapse-all covers every fold at both levels at once — the point of
  // it is to get the whole card open or shut in one action, and a control that only
  // reached the retainers would leave the coverage still hidden.
  const allKeys = [
    ...retainers.map((r) => rKey(r.id)),
    ...jobs.map((j) => jKey(j.id)),
  ];
  const setEverything = (collapsed) => setAll(allKeys, collapsed);

  const reloadPublications = publicationsState.reload;
  const removePublication = async (row) => {
    if (!window.confirm(he.publications.confirmDelete)) return;
    await publicationsApi.remove(row.id);
    reloadPublications();
  };

  const jobProps = {
    isJobOpen: (id) => !isCollapsed(jKey(id), JOB_DEFAULT),
    onToggleJob: (id) => setCollapsed(jKey(id), !isCollapsed(jKey(id), JOB_DEFAULT)),
    onEditPublication: (r) => setEditingPublication(r),
    onDeletePublication: removePublication,
  };

  // A retainer and a project are drawn by the same block — same jobs, agreements, files.
  const engagementBlock = (r) => (
    <RetainerBlock
      key={r.id}
      retainer={r}
      jobs={jobsByRetainer.get(String(r.id)) || []}
      showDone={showDone}
      agreements={agreementsByRetainer.get(String(r.id)) || []}
      archived={archived}
      publicationsFor={publicationsFor}
      workFilesFor={workFilesFor}
      onWorkFilesChange={workFilesState.reload}
      open={!isCollapsed(rKey(r.id), RETAINER_DEFAULT)}
      onToggle={() => setCollapsed(rKey(r.id), !isCollapsed(rKey(r.id), RETAINER_DEFAULT))}
      onChangeAgreements={agreementsState.reload}
      {...jobProps}
    />
  );

  return (
    <AsyncBoundary loading={loading} error={error}>
      <div className="work-tree">
        <div className="card">
          <div className="client-section-head">
            <h2>{he.clientCard.retainersHeading}</h2>
            <div className="row">
              <ViewToggle
                value={showDone}
                onChange={setShowDone}
                offLabel={he.jobs.showOpenJobs}
                onLabel={he.jobs.showDoneJobs}
              />
              <Button small variant="subtle" onClick={() => setArchived((a) => !a)}>
                {archived ? he.clientCard.showAgreementsLive : he.clientCard.showAgreementsArchive}
              </Button>
              <Button small variant="subtle" onClick={() => setEverything(false)}>
                {he.board.expandAll}
              </Button>
              <Button small variant="subtle" onClick={() => setEverything(true)}>
                {he.board.collapseAll}
              </Button>
            </div>
          </div>
          {monthly.length > 0
            ? monthly.map(engagementBlock)
            : <EmptyState text={he.empty.clientRetainers} />}
        </div>

        {/* Below the retainers, one level shallower: these jobs have no retainer above. */}
        {/* One-off projects: engagements billed one fixed amount, with their jobs beneath.
            A job left standalone from before 067 still has nowhere else to be drawn, so it
            follows them here, one level shallower. */}
        <div className="card client-section">
          <h2>{he.clientCard.standaloneHeading}</h2>
          {projects.map(engagementBlock)}
          {projects.length === 0 && standalone.length === 0 && (
            <EmptyState text={he.empty.clientProjects} />
          )}
          {standalone.length > 0
            && standalone.map((job) => (
              <JobBlock
                key={job.id}
                job={job}
                publications={publicationsFor(job.id)}
                workFiles={workFilesFor(job.id)}
                onWorkFilesChange={workFilesState.reload}
                showStream
                open={jobProps.isJobOpen(job.id)}
                onToggle={() => jobProps.onToggleJob(job.id)}
                onEditPublication={jobProps.onEditPublication}
                onDeletePublication={jobProps.onDeletePublication}
              />
            ))}
        </div>
      </div>

      {editingPublication && (
        <PublicationModal
          jobId={editingPublication.job_id}
          publication={editingPublication}
          onClose={() => setEditingPublication(null)}
          onSaved={() => { setEditingPublication(null); reloadPublications(); }}
        />
      )}
    </AsyncBoundary>
  );
}
